import { useState } from "react";
import { Link } from "react-router-dom";

const features = [
  {
    icon: "👟", title: "Live Step Tracking", color: "0,245,196",
    desc: "GPS-powered real-time step counting with sub-meter accuracy. Track distance, calories, speed, and active time. Set custom goals and watch your progress update live on screen.",
    points: ["GPS path recording", "Calories & distance", "Daily/weekly goals", "Streak tracking"],
  },
  {
    icon: "⚔️", title: "Territory Battles", color: "255,107,53",
    desc: "Every path in the city can be claimed. Walk a path 5 days in a row to own it. Defend your territories from rivals who try to reclaim them by out-walking you.",
    points: ["Real-time territory map", "Claim paths by walking", "Defend from rivals", "Territory alerts"],
  },
  {
    icon: "🏆", title: "City Leaderboards", color: "123,97,255",
    desc: "Three competitive leaderboards: total distance, territories owned, and active streaks. Compete within 5km, 100km, or city-wide. Your rank updates in real time.",
    points: ["Distance leaderboard", "Territory leaderboard", "Streak leaderboard", "Real-time rank updates"],
  },
  {
    icon: "🎖️", title: "Rewards & Coins", color: "255,215,0",
    desc: "Earn coins for every step milestone, battle victory, and streak maintained. Claim badges for achievements. Redeem coins for real rewards via the in-app wallet.",
    points: ["Milestone coins", "Battle victory bonuses", "Streak multipliers", "Badge system"],
  },
  {
    icon: "🗺️", title: "Live City Map", color: "0,245,196",
    desc: "See the entire city's territory map overlaid on a dark satellite view. Your claimed paths glow in your color. Enemy territories show up in red. Know your city like a general.",
    points: ["Dark satellite map", "Color-coded territories", "Enemy zones visible", "Real-time updates"],
  },
  {
    icon: "👥", title: "Social & Challenges", color: "255,107,53",
    desc: "Follow friends and rivals. Send direct battle challenges. Check their profile, territories, and stats. Build your reputation as the top walker in your city.",
    points: ["Follow/unfollow users", "Direct challenges", "Public profiles", "Activity feed"],
  },
  {
    icon: "💰", title: "In-App Wallet", color: "123,97,255",
    desc: "Secure digital wallet to store and spend your earned coins. Track transaction history. Transfer coins and redeem them for rewards directly from the app.",
    points: ["Secure wallet", "Transaction history", "Reward redemption", "Coin transfers"],
  },
  {
    icon: "🔔", title: "Smart Notifications", color: "255,215,0",
    desc: "Real-time push alerts when someone challenges you, attacks your territory, or when a battle ends. Never miss a move from your rivals with intelligent notification filtering.",
    points: ["Battle alerts", "Territory attack alerts", "Milestone notifications", "Smart filtering"],
  },
];

function FCard({ icon, title, desc, color, points, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? `rgba(${color},0.08)` : "var(--surface)",
        border: `1px solid ${active ? `rgba(${color},0.5)` : "var(--border)"}`,
        borderRadius: 12,
        padding: "24px 20px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        transform: active ? "scale(1.01)" : "scale(1)",
        boxShadow: active ? `0 12px 36px rgba(0,0,0,0.4), 0 0 24px rgba(${color},0.12)` : "none",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: active ? 16 : 0 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: `rgba(${color},0.12)`,
            border: `1px solid rgba(${color},0.25)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
            boxShadow: active ? `0 0 16px rgba(${color},0.25)` : "none",
            transition: "all 0.3s",
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontFamily: "'Orbitron', monospace", fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
            {title}
          </h3>
          {!active && (
            <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {desc}
            </p>
          )}
        </div>
      </div>
      {active && (
        <div style={{ animation: "fadeInUp 0.3s ease both", marginTop: 12 }}>
          <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.7, marginBottom: 16 }}>{desc}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {points.map((p) => (
              <div key={p} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--text)" }}>
                <span style={{ color: `rgb(${color})`, fontSize: 14, fontWeight: 700 }}>✓</span> {p}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Features() {
  const [active, setActive] = useState(0);

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", paddingTop: 80, overflowX: "hidden" }}>
      <style>{`
        .wb-features-hero {
          padding: 80px 48px 48px;
          text-align: center;
        }

        .wb-features-content {
          padding: 0 48px 80px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .wb-features-list-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
          gap: 18px;
        }

        .wb-features-cta {
          padding: 60px 48px;
          text-align: center;
          max-width: 900px;
          margin: 0 auto;
        }

        /* Tablet (768px - 1024px) */
        @media (max-width: 1024px) {
          .wb-features-hero {
            padding: 60px 28px 40px;
          }
          .wb-features-content {
            padding: 0 28px 60px;
          }
          .wb-features-cta {
            padding: 48px 28px;
          }
        }

        /* Mobile (< 640px) */
        @media (max-width: 640px) {
          .wb-features-hero {
            padding: 48px 16px 32px;
          }
          .wb-features-content {
            padding: 0 16px 48px;
          }
          .wb-features-list-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .wb-features-cta {
            padding: 40px 16px;
          }
        }
      `}</style>

      {/* Hero */}
      <section className="wb-features-hero">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "rgba(0,245,196,0.07)", border: "1px solid rgba(0,245,196,0.2)", padding: "6px 16px", borderRadius: 100, marginBottom: 20, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--neon)", letterSpacing: 2, textTransform: "uppercase" }}>
          ⚡ All Features
        </div>
        <h1 style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(28px, 5.5vw, 60px)", fontWeight: 900, marginBottom: 18, lineHeight: 1.05 }}>
          <span style={{ color: "var(--text)" }}>Everything You Need to</span><br />
          <span style={{ background: "linear-gradient(90deg,var(--neon),var(--neon3),var(--neon2))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", backgroundSize: "200%", animation: "gradShift 3s linear infinite" }}>Rule the City</span>
        </h1>
        <p style={{ color: "var(--muted)", fontSize: "clamp(15px, 2vw, 17px)", maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
          Click any feature to explore it in detail. 8 powerful tools built for the serious fitness competitor.
        </p>
      </section>

      {/* Feature Grid */}
      <section className="wb-features-content">
        <div className="wb-features-list-grid">
          {features.map((f, i) => (
            <FCard key={f.title} {...f} active={active === i} onClick={() => setActive(i)} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="wb-features-cta">
        <h2 style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(22px, 4vw, 38px)", fontWeight: 900, color: "var(--text)", marginBottom: 14 }}>
          Ready to Start?
        </h2>
        <p style={{ color: "var(--muted)", marginBottom: 28, fontSize: 16 }}>All features are free to use. No subscriptions, no hidden fees.</p>
        <Link to="https://play.google.com/store/apps/details?id=com.bluespace.walkbattle" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "var(--neon)", color: "var(--bg)", padding: "16px 36px", borderRadius: 6, fontFamily: "'Orbitron', monospace", fontSize: 11, fontWeight: 800, letterSpacing: 2, textDecoration: "none", textTransform: "uppercase", boxShadow: "0 0 24px rgba(0,245,196,0.35)", transition: "all 0.3s" }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 40px rgba(0,245,196,0.6)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 24px rgba(0,245,196,0.35)"; e.currentTarget.style.transform = "none"; }}>
          📲 Download Free on Play Store
        </Link>
      </section>
    </div>
  );
}

